import { z } from 'zod';
import { withinJsonLength } from './common';
import { SAFE_USER_ID_REGEX } from '../constants';
import { AGENT_PARAMETER_NAME_REGEX } from './setup/agents';
import { messageAttachment, MAX_ATTACHMENTS_PER_MESSAGE } from './common';

// ---------------------------------------------------------------------------
// Common param
// ---------------------------------------------------------------------------

export const conversationIdParam = z.object({
  id: z.string().uuid(),
});

/**
 * The hub a conversation-id-only route (`POST /:id/reopen`, both `/observability` reads) reads the
 * conversation in (gh #4757). These routes once learned the hub from the conversation's own
 * ConversationDO row; the client now names the hub, and the conversation's row must name the
 * same one. Optional on the wire only so a request from an older client, which names no hub, is
 * answered as an unknown conversation (404, nothing read) rather than a 400: the CLI's
 * observability command already follows that 404 to the hub-scoped archive routes.
 */
export const conversationHubQuery = z.object({
  hub_id: z.string().uuid().optional(),
});

// ---------------------------------------------------------------------------
// Claim
// ---------------------------------------------------------------------------

export const claimBody = z.object({
  hub_id: z.string().uuid(),
  new_team_id: z.string().uuid(),
  new_status: z.string().default('team'),
  new_hub_team_user_id: z.string().optional(),
  new_responder_type: z.string().default('team'),
  previous_status: z.string().optional(),
  previous_responder_type: z.string().optional(),
  previous_agent_id: z.string().uuid().optional(),
  previous_team_id: z.string().uuid().optional(),
  previous_hub_team_user_id: z.string().optional(),
  channel_id: z.string().uuid().optional(),
  hub_type: z.enum(['chat', 'task']).default('chat'),
  // SAFE_USER_ID_REGEX bounds alphabet+length (1–64 of [A-Za-z0-9_-]) so
  // SQL/path/scope-key injection vectors can't reach downstream consumers.
  // Matches `deleteUserConversationsBody.user_id` below.
  user_id: z.string().regex(SAFE_USER_ID_REGEX).optional(),
});

// ---------------------------------------------------------------------------
// Release
// ---------------------------------------------------------------------------

export const releaseBody = z.object({
  hub_id: z.string().uuid(),
  new_status: z.string().optional(),
  new_hub_team_user_id: z.string().optional(),
  new_team_id: z.string().uuid().optional(),
  new_responder_type: z.string().optional(),
  previous_status: z.string().optional(),
  previous_responder_type: z.string().optional(),
  previous_agent_id: z.string().uuid().optional(),
  previous_team_id: z.string().uuid().optional(),
  previous_hub_team_user_id: z.string().optional(),
  channel_id: z.string().uuid().optional(),
  hub_type: z.enum(['chat', 'task']).default('chat'),
  user_id: z.string().optional(),
});

// ---------------------------------------------------------------------------
// Transfer
// ---------------------------------------------------------------------------

export const transferBody = z.object({
  hub_id: z.string().uuid(),
  new_status: z.string().optional(),
  new_responder_type: z.string().optional(),
  new_agent_id: z.string().uuid().optional(),
  new_team_id: z.string().uuid().optional(),
  new_hub_team_user_id: z.string().optional(),
  previous_status: z.string().optional(),
  previous_responder_type: z.string().optional(),
  previous_agent_id: z.string().uuid().optional(),
  previous_team_id: z.string().uuid().optional(),
  previous_hub_team_user_id: z.string().optional(),
  channel_id: z.string().uuid().optional(),
  hub_type: z.enum(['chat', 'task']).default('chat'),
  user_id: z.string().optional(),
});

// ---------------------------------------------------------------------------
// Close
// ---------------------------------------------------------------------------

export const closeBody = z.object({
  hub_id: z.string().uuid(),
  previous_status: z.string().optional(),
  previous_responder_type: z.string().optional(),
  previous_agent_id: z.string().uuid().optional(),
  previous_team_id: z.string().uuid().optional(),
  previous_hub_team_user_id: z.string().optional(),
  channel_id: z.string().uuid().optional(),
  hub_type: z.enum(['chat', 'task']).default('chat'),
  user_id: z.string().optional(),
});

// ---------------------------------------------------------------------------
// Kanban Update
// ---------------------------------------------------------------------------

/**
 * Wire payload for `kanbanUpdateBody.additional_context` and the equivalent
 * MCP `update_kanban_status` tool input. Server-side validates the payload
 * shape against the target kanban status's `additional_context_schema`
 * (a JSON Schema). Bounded to 16 KB JSON-serialized — mirrors the same cap
 * on the schema field itself in `setup/kanban.ts`.
 *
 * Single source of truth for the `additional_context` shape: consumed by
 * `kanbanUpdateBody` below (the REST `PATCH /:id/kanban` contract). Exported
 * so any client constructing the same payload validates against one schema.
 */
export const additionalContextPayloadSchema = z
  .record(z.unknown())
  // `withinJsonLength`, not a bare `JSON.stringify(...).length`: the payload is
  // arbitrary caller JSON, and a value too deep to re-serialize would throw
  // RangeError here rather than being rejected (see its doc).
  .refine((r) => withinJsonLength(r, 16384), {
    message: 'additional_context payload must be at most 16384 bytes when JSON-serialized',
  });

export const kanbanUpdateBody = z.object({
  hub_id: z.string().uuid(),
  // 50-char slug cap + headroom; bounds the "unknown kanban status" 400 echo.
  new_kanban_status: z.string().max(64),
  previous_kanban_status: z.string().max(64).optional(),
  channel_id: z.string().uuid().optional(),
  hub_type: z.enum(['chat', 'task']).default('chat'),
  user_id: z.string().optional(),
  // Bounded, not format-validated: the server normalizes both accepted shapes
  // (zoned instant, or zone-less wall clock resolved in the hub timezone) to
  // canonical ISO-8601 UTC and rejects the rest with a specific error. The cap
  // exists because this value is now PERSISTED on two DOs and re-serialized into
  // every support viewer's bootstrap — an unbounded string would be stored and
  // fanned out per conversation. 64 chars fits any ISO-8601 form with headroom.
  scheduled_event_date: z.string().max(64).optional(),
  additional_context: additionalContextPayloadSchema.optional(),
  event_description: z.string().optional(),
  // Closing outcome slug — required by the server only when the target is the
  // terminal status AND it declares `outcomes`. 50-char slug cap + headroom.
  outcome: z.string().max(64).optional(),
});

// Post-hoc annotation (business outcome) set on a conversation after it ends —
// e.g. customer_purchased. `key` becomes a `data.annotations.<key>` analytics
// dimension, so it must be a safe SQL identifier. `type` drives both value
// coercion and how the auto-registered dimension aggregates.
export const annotateConversationBody = z.object({
  hub_id: z.string().uuid(),
  key: z
    .string()
    .regex(
      AGENT_PARAMETER_NAME_REGEX,
      'key must be a valid identifier (letter/underscore start; letters, digits, underscore; max 64 chars)',
    ),
  value: z.union([z.string().max(1024), z.number(), z.boolean()]),
  type: z.enum(['numeric', 'categorical', 'text']).default('categorical'),
});

// ---------------------------------------------------------------------------
// Observability
// ---------------------------------------------------------------------------

export const observabilityParams = z.object({
  conversationId: z.string().uuid(),
  messageId: z.string().uuid(),
});

// List variant — same conversationId shape as `observabilityParams`, derived
// via `.pick` so the field stays in lockstep with the single-message route.
export const observabilityListParams = observabilityParams.pick({ conversationId: true });

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

/** Result of a conversation operation (claim, release, transfer, close) */
export interface ConversationOperationResponse {
  success: true;
  operation_id: string;
  operation_type: 'claim' | 'release' | 'transfer' | 'close';
  conversation_id: string;
}

/** Result of a kanban status update operation */
export interface KanbanUpdateResponse {
  success: true;
  operation_id: string;
  operation_type: 'update_kanban_status';
  conversation_id: string;
  kanban_status_updated_at?: string;
  /** Set when the new kanban_status is terminal and the conversation auto-closed. */
  closed?: boolean;
}

/** Result of a post-hoc annotation set operation */
export interface AnnotateConversationResponse {
  success: true;
  operation_type: 'set_annotation';
  conversation_id: string;
  key: string;
}

/** Result of a conversation reopen operation */
export interface ReopenConversationResponse {
  data: Record<string, unknown>;
}

/** Observability data for a message */
export interface ObservabilityResponse {
  data: import('../observability/types').LLMObservabilityRecord;
}

/**
 * List of per-message observability summaries for a conversation.
 * Carries the metadata half of each record only (timestamp, latency, agent, tool counts)
 * — drill into a specific message for full provider/model/tokens/messages.
 */
export interface ObservabilityListResponse {
  data: {
    hub_id: string;
    conversation_id: string;
    entries: import('../observability/types').WayAIMetadataRecord[];
    truncated: boolean;
  };
}

// ---------------------------------------------------------------------------
// Delete User Conversations
//
// Hub-admin only. `mode='current'` wipes a single conversation; `mode='all'`
// wipes every conversation for the target user on this hub AND clears that
// user's user-scoped hub state (the user's full footprint — see gh #3426).
// Deletion is hub-scoped — same `user_id` on a sibling hub is untouched.
// Permanent.
// ---------------------------------------------------------------------------

export const deleteUserConversationsBody = z.discriminatedUnion('mode', [
  z.object({
    hub_id: z.string().uuid(),
    user_id: z.string().regex(SAFE_USER_ID_REGEX),
    mode: z.literal('all'),
  }),
  z.object({
    hub_id: z.string().uuid(),
    user_id: z.string().regex(SAFE_USER_ID_REGEX),
    mode: z.literal('current'),
    conversation_id: z.string().uuid(),
  }),
]);
export type DeleteUserConversationsBody = z.infer<typeof deleteUserConversationsBody>;

export const deleteUserConversationsResponse = z.object({
  success: z.literal(true),
  /** Durable erasure marker/workflow identity. */
  erasure_id: z.string().uuid(),
  /** Deletion continues asynchronously under the durable workflow. */
  status: z.literal('pending'),
  /** Final counts are recorded in the audit marker; an accepted response reports 0. */
  deleted_count: z.number().int().nonnegative(),
  /**
   * Accepted responses report 0. The durable audit records the final number of
   * user-scoped slugs cleared for mode='all'; mode='current' never clears state.
   */
  cleared_state_count: z.number().int().nonnegative(),
});
export type DeleteUserConversationsResponse = z.infer<typeof deleteUserConversationsResponse>;

// ---------------------------------------------------------------------------
// Send Message (hub testing — preview or production)
// ---------------------------------------------------------------------------

export const sendMessageBody = z.object({
  hub_id: z.string().uuid(),
  /**
   * Optional ONLY when `attachments` carries the payload — see the refinement below.
   * An attachment-only message (a photo with no caption) is the ordinary WhatsApp
   * gesture, so the endpoint that exists to rehearse inbound flows has to express it.
   * Mirrors `appMessageBody`, where content is optional for the same reason.
   */
  message: z.string().min(1).max(50000).optional(),
  /** For chat hubs: optional — automatically continues the user's active conversation (or creates one).
   *  For task hubs: required to continue an existing conversation. Omit to start a new task conversation. */
  conversation_id: z.string().uuid().optional(),
  /**
   * Base64 attachments, delivered over the hub's `app` channel exactly as a real
   * channel delivers them. Total bytes are bounded by the route's bodyLimit —
   * `/api/*` carries no global one (only `/webhooks/*` and `/channels/*` do), so the
   * route declares its own rather than inheriting an unbounded upload path.
   *
   * AUDIO attachments (`isAudioAttachmentFileName`) are routed through the hub's
   * STT connection like a real voice note — the agent receives the transcript, not
   * the file. At most ONE audio attachment per message (the DO transcribes only the
   * first; extras would be silently invisible to the agent), each capped at
   * `MAX_AUDIO_FILE_BASE64_BYTES` base64 — both rejected pre-write with a 400.
   */
  attachments: z.array(messageAttachment).max(MAX_ATTACHMENTS_PER_MESSAGE).optional(),
}).refine(
  (b) => (b.message?.length ?? 0) > 0 || (b.attachments?.length ?? 0) > 0,
  { message: 'message or attachments required' },
);

export interface SendMessageResponse {
  conversation_id: string;
  hub_type: 'chat' | 'task';
  user_message_id: string;
  agent_reply: {
    message_id: string;
    message_text: string;
  } | null;
  /**
   * - `replied` — agent produced a reply (in `agent_reply`).
   * - `skipped` — AI mode off / responder is team without copilot (no reply expected).
   * - `empty_response` — turn completed but produced no text; do NOT retry.
   * - `error` — genuine failure; the user message was stored, but no reply is coming.
   * - `processing` — the turn is running/continuing asynchronously (a retryable
   *   provider error rescheduled it, or it crossed the synchronous wall-clock cap
   *   while still progressing). The reply arrives out-of-band via live-update /
   *   on re-poll once the DO turn completes — clients should wait/poll, NOT retry.
   * - `quota_exceeded` — the org's operations quota is exhausted; the turn was
   *   refused (not an error). No reply is coming until quota resets/upgrades.
   */
  status: 'replied' | 'skipped' | 'error' | 'empty_response' | 'processing' | 'quota_exceeded';
  status_reason?: string;
}

// ---------------------------------------------------------------------------
// List Conversations (admin)
// ---------------------------------------------------------------------------

export const listConversationsQuery = z
  .object({
    hub_id: z.string().uuid(),
    status: z.enum(['agent', 'team', 'ended']).optional(),
    /**
     * Last-activity window, ISO-8601 UTC, both bounds inclusive. Filters the hub
     * conversation index on its last activity (`last_message_at`, falling back to
     * `created_at` for a row that has no message yet) — NOT on conversation start.
     *
     * Activity is the axis a support-queue check needs and the one this listing
     * already sorts by and returns as `updated_at`: `activity_to` alone answers
     * "team conversations with nothing since X" (idle), which a start-time window
     * cannot express at all. The ClickHouse-backed analytics list
     * (`POST /api/analytics/conversations`) windows `created_at` instead and only
     * ever holds ENDED conversations, so it can never answer this for `agent`/`team`.
     */
    activity_from: z.string().datetime().optional(),
    activity_to: z.string().datetime().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    offset: z.coerce.number().int().min(0).default(0),
  })
  .refine(
    (q) =>
      q.activity_from === undefined ||
      q.activity_to === undefined ||
      Date.parse(q.activity_from) <= Date.parse(q.activity_to),
    { message: 'activity_from must not be after activity_to', path: ['activity_from'] },
  );

const flagSourceSchema = z.enum(['evaluator', 'monitor', 'both']).nullable();

export const getConversationsResponse = z.object({
  conversations: z.array(
    z.object({
      conversation_id: z.string(),
      conversation_status: z.string(),
      current_responder_type: z.string().nullable(),
      access_status: z.string(),
      kanban_status: z.string().nullable(),
      /** Closing disposition of the terminal kanban transition; cleared on reopen. */
      outcome: z.string().nullable(),
      created_at: z.string().datetime(),
      updated_at: z.string().datetime().nullable(),
      user_name: z.string().nullable(),
      user_email: z.string().nullable(),
      is_flagged: z.boolean(),
      flag_source: flagSourceSchema,
    }),
  ),
  total: z.number(),
});
export type GetConversationsResponse = z.infer<typeof getConversationsResponse>;

// ---------------------------------------------------------------------------
// Get Conversation Detail (with messages)
// ---------------------------------------------------------------------------

export const getConversationDetailParams = z.object({
  id: z.string().uuid(),
});

export const getConversationDetailQuery = z.object({
  hub_id: z.string().uuid(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const getConversationDetailResponse = z.object({
  conversation: z.object({
    conversation_id: z.string(),
    conversation_status: z.string(),
    current_responder_type: z.string().nullable(),
    access_status: z.string(),
    kanban_status: z.string().nullable(),
    /** Closing disposition of the terminal kanban transition; cleared on reopen. */
    outcome: z.string().nullable(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime().nullable(),
    user_name: z.string().nullable(),
    user_email: z.string().nullable(),
    is_flagged: z.boolean(),
    flag_source: flagSourceSchema,
    /**
     * Where this conversation sits in its owner's archived timeline — derived per
     * request from HubDO's `conversation_index`, never stored. Support-only, like
     * the other enriched fields on this response, and absent for a caller without
     * support access. A neighbour may be older than `conversation_retention_days`,
     * in which case its ConversationDO is gone and this endpoint 404s for it.
     */
    previous_conversation_id: z.string().nullable().optional(),
    next_conversation_id: z.string().nullable().optional(),
  }),
  messages: z.array(
    z.object({
      message_id: z.string(),
      message_text: z.string().nullable(),
      sender_type: z.string(),
      receiver_type: z.string().nullable(),
      created_at: z.string().datetime(),
      message_type: z.string().optional(),
      channel_type: z.string().nullable().optional(),
      keep_in_history: z.boolean().optional(),
      agent_id: z.string().nullable().optional(),
      sender_id: z.string().nullable().optional(),
      receiver_id: z.string().nullable().optional(),
      sender_description: z.string().optional(),
      receiver_description: z.string().optional(),
      metadata: z.record(z.unknown()).nullable().optional(),
      message_status: z.string().nullable().optional(),
    }),
  ),
  total_messages: z.number(),
});
export type GetConversationDetailResponse = z.infer<typeof getConversationDetailResponse>;

// ---------------------------------------------------------------------------
// WhatsApp template picker (closed-window team re-engagement)
//
// When a WhatsApp conversation is past Meta's 24h messaging window, a free-form
// team reply is blocked. An approved WhatsApp template is the only compliant
// re-engagement path. These contracts back the picker: list the conversation's
// approved templates, then send one with body variables filled in.
// ---------------------------------------------------------------------------

export const listWhatsAppTemplatesQuery = z.object({
  hub_id: z.string().uuid(),
});

export const sendWhatsAppTemplateBody = z.object({
  hub_id: z.string().uuid(),
  /** HubDO outbound_template.template_id. */
  template_id: z.string().min(1).max(128),
  /** The conversation's end user (customer). Bounded like `claimBody.user_id`. */
  user_receiver_id: z.string().regex(SAFE_USER_ID_REGEX),
  /** Body-variable values: positional array or key→value record. Bounded so an
   *  authenticated caller can't post an oversized payload to the transcript. */
  variables: z
    .union([
      z.array(z.string().max(2048)).max(50),
      z
        .record(z.string().max(128), z.string().max(2048))
        .refine((r) => Object.keys(r).length <= 50, 'too many variables'),
    ])
    .optional(),
});

/** One approved WhatsApp template, shaped for the picker UI. */
export interface WhatsAppTemplatePickerItem {
  template_id: string;
  display_name: string;
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  language: string;
  /** BODY component text, with `{{N}}` / `{{name}}` placeholders intact. */
  body_text: string;
  parameter_format: 'POSITIONAL' | 'NAMED';
  /** Distinct variable keys in render order — drives the fill-in form. */
  variables: string[];
}

export interface ListWhatsAppTemplatesResponse {
  data: WhatsAppTemplatePickerItem[];
}

/**
 * Send result on success. Failures return a non-2xx with the standard error
 * envelope (`{ error, message }`) whose `error` carries a stable code the
 * picker maps to a message — e.g. `template_not_approved`, `conversation_ended`,
 * `missing_variables`, `meta_rejected:<n>`.
 */
export interface SendWhatsAppTemplateResponse {
  data: { message_id: string; channel_message_sid: string | null; rendered_text: string };
}
